import React, { useEffect, useRef, useState } from 'react';
import { touch } from '../../style-utils/media-queries';
import styled from '@emotion/styled';
import { Pop } from '../../motion';
import { DURATION, SCALE } from '../../motion/tokens';
import {
  duration,
  easing,
  gradient,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../style-utils/theme';
import { spin } from '../../style-utils/motion';

export type AppButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive';
export type AppButtonSize = 'normal' | 'compact';

interface StyleProps {
  $variant: AppButtonVariant;
  $size: AppButtonSize;
  $fullWidth: boolean;
  $iconOnly: boolean;
}

const fillFor = ({ $variant }: StyleProps): string => {
  switch ($variant) {
    case 'primary':
      return gradient.primary;
    // alertStrong, NOT alert: this fill carries a white label, and #F0564A is
    // only 3.42:1 under white. palette.alert stays correct for the border, the
    // dot and the icon of the same component, where the floor is 3:1.
    case 'destructive':
      return palette.alertStrong;
    case 'secondary':
      return palette.surface;
    default:
      return 'transparent';
  }
};

const colorFor = ({ $variant }: StyleProps): string => {
  if ($variant === 'primary' || $variant === 'destructive') {
    return palette.white;
  }

  /* lagoon, not the primary teal. A ghost button is coloured TEXT on a light
     ground at 13px — "Revoke", "Cancel subscription" — and palette.teal measures
     3.20:1 there, under the 4.5:1 that size needs. Deep Lagoon is the same hue at
     4.9:1, which is exactly the case design_system.md §2.1 reserves it for. */
  return $variant === 'ghost' ? palette.lagoon : palette.ink;
};

const borderFor = ({ $variant }: StyleProps): string => {
  return $variant === 'secondary'
    ? `1px solid ${palette.lineStrong}`
    : '1px solid transparent';
};

const heightFor = ({ $size }: Pick<StyleProps, '$size'>): string => {
  return $size === 'compact'
    ? layout.compactControlHeight
    : layout.buttonHeight;
};

const ButtonRoot = styled.button<StyleProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  width: ${({ $fullWidth, $iconOnly }) =>
    $fullWidth ? '100%' : $iconOnly ? layout.touchTarget : 'auto'};
  height: ${({ $iconOnly, $size }) =>
    $iconOnly ? layout.touchTarget : heightFor({ $size })};
  min-width: ${({ $iconOnly }) => ($iconOnly ? layout.touchTarget : '0')};
  padding: ${({ $iconOnly, $size }) =>
    $iconOnly
      ? '0'
      : $size === 'compact'
      ? `0 ${spacing.sm}`
      : `0 ${spacing.md}`};
  border: ${borderFor};
  border-radius: ${radius.sm};
  background: ${fillFor};
  color: ${colorFor};
  box-shadow: ${({ $variant }) =>
    $variant === 'primary' ? shadow.brand : 'none'};
  font-family: ${typography.ui};
  font-size: ${({ $size }) =>
    $size === 'compact'
      ? `${typography.size.xs}px`
      : `${typography.size.sm}px`};
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  & > svg {
    width: ${spacing.md};
    height: ${spacing.md};
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: ${({ $variant }) =>
      $variant === 'primary'
        ? gradient.primaryHover
        : $variant === 'destructive'
        ? // DARKER than the resting alertStrong, not lighter. The old hover was
          // palette.alert, which is now paler than the resting fill — a hover
          // that reduces contrast reads as the control going inactive.
          palette.alertDeep
        : palette.tealTintSoft};
    border-color: ${({ $variant }) =>
      $variant === 'secondary' ? palette.teal : 'transparent'};
    box-shadow: ${({ $variant }) =>
      $variant === 'primary' ? shadow.brandHover : shadow.sm};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* THE TOUCH FLOOR. The compact size is a DENSITY choice for a mouse, not a
     licence to ship a 32px target to a thumb — and this product's primary
     device is a desk tablet.
     height:auto is load-bearing: the rule above sets an explicit height, and a
     min-height cannot grow a box whose height is already fixed.
     NO BACKTICKS IN THIS COMMENT — it sits inside a styled template literal, so
     one silently terminates the string and the file stops parsing. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  &:disabled {
    cursor: not-allowed;
    background: ${({ $variant }) =>
      $variant === 'ghost' ? 'transparent' : palette.surfaceMuted};
    border-color: ${({ $variant }) =>
      $variant === 'secondary' ? palette.line : 'transparent'};
    color: ${palette.slateLight};
    box-shadow: none;
    transform: none;
  }
`;

const Content = styled.span<{ $hidden: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

const LoadingRing = styled.span`
  position: absolute;
  width: ${spacing.sm};
  height: ${spacing.sm};
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: ${radius.circle};
  animation: ${spin} ${duration.slow} linear infinite;
`;

/* THE SUCCESS TICK. Same slot as the ring — absolute, over the hidden label —
   so "Save" → tick → "Save" never changes the button's width; the label keeps
   holding the box open underneath. */
const SuccessMark = styled.span`
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: currentColor;

  & > * > svg,
  & > svg {
    width: ${spacing.md};
    height: ${spacing.md};
  }
`;

/** A stroke tick in currentColor. Shared with NavButton. */
export const SuccessTick: React.FC = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M3 8.5l3 3 7-7"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * HOLD THE TICK FOR AT LEAST `DURATION.emphasis`.
 *
 * A caller usually flips `success` on for one render (the save resolved) and
 * off again when the form resets, which can be the same frame. The tick has to
 * be SEEN to mean anything, so it stays for the emphasis tempo from the moment
 * `success` turns on — and longer, for as long as the caller keeps it true.
 * The timer lives in a ref so a caller dropping `success` early does not also
 * cancel the hold. Shared with NavButton.
 */
export const useSuccessHold = (success: boolean): boolean => {
  const [held, setHeld] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!success) return;
    setHeld(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHeld(false), DURATION.emphasis);
  }, [success]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return success || held;
};

export interface AppButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  fullWidth?: boolean;
  iconOnly?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  /** Swap the label for a popping tick, holding the button's width. Held for
   *  at least DURATION.emphasis after it turns on; `loading` wins over it. */
  success?: boolean;
  successLabel?: string;
}

export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'normal',
      fullWidth = false,
      iconOnly = false,
      loading = false,
      loadingLabel = 'Working',
      success = false,
      successLabel = 'Done',
      disabled,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => {
    const showTick = useSuccessHold(success) && !loading;

    return (
      <ButtonRoot
        {...rest}
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={
          loading ? loadingLabel : showTick ? successLabel : rest['aria-label']
        }
        data-success={showTick || undefined}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $iconOnly={iconOnly}
      >
        <Content $hidden={loading || showTick}>{children}</Content>
        {loading ? <LoadingRing aria-hidden="true" /> : null}
        {showTick ? (
          <SuccessMark aria-hidden="true">
            <Pop>
              <SuccessTick />
            </Pop>
          </SuccessMark>
        ) : null}
      </ButtonRoot>
    );
  }
);

AppButton.displayName = 'AppButton';
